import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { supabase } from '../../services/supabase'
// @ts-ignore - types are provided as ambient declarations
import { loadMoov } from '@moovio/moov-js'

interface MoovTosGateProps {
  moovAccountId: string
  onAccepted: () => void
}

const CONTAINER_ID = 'moov-tos-drop-container'

// Moov only honours our access tokens on the domains registered with Moov
// (token aud: creator.shoutout.us and shoutout.us). On any other host, e.g.
// shoutout.fans, the Drop silently gets a ToS token WITHOUT OAuth and
// moov-accept-tos then fails ("tos token must be created using an oauth
// token"). So off those hosts the terms step sends the creator to the creator
// dashboard instead. `tab=payouts` survives the dashboard's sign-in handoff.
const MOOV_HOSTS = ['creator.shoutout.us', 'shoutout.us']
const PAYOUTS_ON_CREATOR_HOST = 'https://creator.shoutout.us/dashboard?tab=payouts'

function onRegisteredMoovHost(): boolean {
  if (typeof window === 'undefined') return true
  return MOOV_HOSTS.includes(window.location.hostname)
}

/**
 * Renders Moov's Terms-of-Service Drop so the creator accepts Moov's privacy
 * policy + platform agreement. The Drop generates a ToS token in the creator's
 * own browser (capturing their real IP), which we then patch server-side via
 * the `moov-accept-tos` function. This unblocks the `send-funds` capability and,
 * with it, bank-account linking for payouts.
 */
const MoovTosGate: React.FC<MoovTosGateProps> = ({ moovAccountId, onAccepted }) => {
  const onMoovHost = onRegisteredMoovHost()
  const [loading, setLoading] = useState(onMoovHost)
  const [tosToken, setTosToken] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    if (!onMoovHost) {
      return () => {
        mounted.current = false
      }
    }
    ;(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('moov-gateway-token')
        if (error || !data?.token) {
          throw new Error(data?.error || 'Could not initialize secure terms')
        }
        const moov = await loadMoov(data.token)
        if (!moov) throw new Error('Moov.js failed to load')
        const drops = await moov.drops()
        drops.termsOfService(
          {
            token: data.token,
            customActionCopy: 'By continuing',
            onTermsOfServiceTokenReady: (t: string) => {
              if (mounted.current) setTosToken(t)
            },
            onTermsOfServiceTokenError: () => {
              if (mounted.current) setError('Could not load Moov terms. Please retry.')
            }
          },
          CONTAINER_ID
        )
        if (mounted.current) setLoading(false)
      } catch (e: any) {
        if (mounted.current) {
          setError(e?.message || 'Failed to load terms')
          setLoading(false)
        }
      }
    })()
    return () => {
      mounted.current = false
    }
  }, [onMoovHost])

  const handleAccept = async () => {
    if (!tosToken) return
    setSubmitting(true)
    try {
      const { data, error } = await supabase.functions.invoke('moov-accept-tos', {
        body: { tosToken, moovAccountId }
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      toast.success('Terms accepted')
      onAccepted()
    } catch (e: any) {
      toast.error(e?.message || 'Could not record acceptance')
      setSubmitting(false)
    }
  }

  if (!onMoovHost) {
    return (
      <div className="max-w-md mx-auto py-8 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Finish payout setup in your creator dashboard</h3>
        <p className="text-gray-600 mb-6">
          Our payment processor only accepts its terms on creator.shoutout.us. Open your dashboard there, sign in if
          asked, and pick up payout setup where you left off. Nothing you've done so far is lost.
        </p>
        <a
          href={PAYOUTS_ON_CREATOR_HOST}
          className="inline-block px-8 py-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl w-full"
          style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
        >
          Continue at creator.shoutout.us
        </a>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto py-8 text-center">
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Accept Payment Terms</h3>
      <p className="text-gray-600 mb-6">
        To receive payouts, review and accept Moov's Privacy Policy and Platform Agreement.
        Moov is our payment processor.
      </p>

      {loading && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-500 text-sm">Loading terms…</p>
        </div>
      )}

      {/* Moov renders the privacy policy + platform agreement here */}
      <div
        id={CONTAINER_ID}
        className="text-left bg-gray-50 border border-gray-200 rounded-lg p-4 mb-5 min-h-[60px]"
      />

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <button
        onClick={handleAccept}
        disabled={!tosToken || submitting}
        className="px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed w-full"
        style={{ background: 'linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)' }}
      >
        {submitting ? 'Saving…' : !tosToken ? 'Loading terms…' : 'Agree & Continue'}
      </button>
    </div>
  )
}

export default MoovTosGate
